import {FormEvent, useEffect, useRef, useState} from "react"
import {MessageCircle, Send, Sparkles, X} from "lucide-react"
import MarkdownMessage from "./MarkdownMessage.tsx"

type Message = {
  role: "user" | "assistant"
  content: string
}

interface ChatWidgetProps {
  agent: "website" | "resume"
  placeholder?: string
  greeting?: string
  starterPrompts?: string[]
  floating?: boolean
  opaque?: boolean
  panelTitle?: string
  panelKicker?: string
  panelDescription?: string
  aiModel?: string
}

function ChatPanel({
  agent,
  placeholder = "Ask me anything...",
  greeting,
  starterPrompts = [],
  panelTitle = "Ask Gradey",
  panelKicker = "AI assistant",
  panelDescription,
  aiModel,
}: Omit<ChatWidgetProps, "floating" | "opaque">) {
  const [messages, setMessages] = useState<Message[]>(
    greeting ? [{role: "assistant", content: greeting}] : []
  )
  const [input, setInput] = useState("")
  const [chatId, setChatId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [rateLimited, setRateLimited] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: "smooth", block: "end"})
  }, [messages, loading])

  async function ensureChat(): Promise<number> {
    if (chatId) return chatId

    const res = await fetch("/api/chats", {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify({agent}),
    })
    const data = await res.json()
    setChatId(data.chat_id)
    return data.chat_id
  }

  async function sendMessage(content: string) {
    if (!content || loading) return

    setInput("")
    setMessages(prev => [...prev, {role: "user", content}])
    setLoading(true)

    try {
      const id = await ensureChat()
      const res = await fetch(`/api/chats/${id}/messages`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({content, agent}),
      })
      const data = await res.json()

      if (res.status === 429) {
        setMessages(prev => [...prev, {role: "assistant", content: data.content}])
        setRateLimited(true)
        return
      }

      setMessages(prev => [...prev, {role: "assistant", content: data.content}])
    } catch {
      setMessages(prev => [
        ...prev,
        {role: "assistant", content: "Sorry, something went wrong. Please try again."},
      ])
    } finally {
      setLoading(false)
      inputRef.current?.focus()
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    await sendMessage(input.trim())
  }

  function fillStarterPrompt(prompt: string) {
    if (loading) return

    setInput(prompt)
    inputRef.current?.focus()
  }

  const hasUserMessages = messages.some(message => message.role === "user")
  const showStarterPrompts = starterPrompts.length > 0 && !hasUserMessages

  return (
    <div className="flex w-full flex-col overflow-hidden rounded-sm border-2 border-gray-950 bg-white shadow-[8px_8px_0_rgb(17_24_39)]">
      <div className="flex items-start justify-between gap-4 border-b-2 border-gray-950 bg-gray-950 px-4 py-3 text-white">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-[0.24em] text-gray-300">{panelKicker}</p>
          <h2 className="text-lg font-black uppercase leading-none tracking-normal">{panelTitle}</h2>
          {panelDescription && (
            <p className="mt-2 max-w-sm text-xs font-medium leading-relaxed text-gray-300">{panelDescription}</p>
          )}
          {aiModel && (
            <p className="mt-2 text-[10px] font-black uppercase tracking-[0.18em] text-gray-400">
              powered by {aiModel}
            </p>
          )}
        </div>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-white/20 bg-white/10">
          <Sparkles className="h-5 w-5" aria-hidden="true" />
        </div>
      </div>

      <div className="h-[26rem] max-h-[60vh] flex-1 space-y-3 overflow-y-auto bg-gray-50 px-4 pt-4 pb-16">
        {messages.length === 0 && (
          <p className="pt-8 text-center text-sm text-gray-500">
            Start a conversation...
          </p>
        )}
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[84%] rounded-sm border-2 px-3.5 py-2 text-left text-sm leading-relaxed ${
                msg.role === "user"
                  ? "border-gray-950 bg-gray-950 text-white"
                  : "border-gray-300 bg-white text-gray-800 shadow-sm"
              }`}
            >
              {msg.role === "assistant" ? <MarkdownMessage content={msg.content} /> : msg.content}
            </div>
          </div>
        ))}
        {showStarterPrompts && (
          <div className="space-y-2 pt-1">
            {starterPrompts.map(prompt => (
              <button
                key={prompt}
                type="button"
                onClick={() => fillStarterPrompt(prompt)}
                disabled={loading}
                className="block w-full rounded-sm border-2 border-gray-300 bg-white px-3 py-2 text-left text-sm font-medium leading-snug text-gray-700 transition-colors hover:border-gray-950 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {prompt}
              </button>
            ))}
          </div>
        )}
        {loading && (
          <div className="flex justify-start">
            <div className="rounded-sm border-2 border-gray-300 bg-white px-3.5 py-2 text-sm text-gray-400 shadow-sm">
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: "0ms"}} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: "150ms"}} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: "300ms"}} />
              </span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {rateLimited ? (
        <div className="border-t-2 border-gray-950 p-3 text-center text-sm font-semibold text-gray-500">
          Message limit reached
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex gap-2 border-t-2 border-gray-950 bg-white p-3">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={placeholder}
            disabled={loading}
            className="min-w-0 flex-1 rounded-sm border-2 border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:border-gray-950 focus:outline-none focus:ring-0 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="flex h-10 shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-sm border-2 border-gray-950 bg-gray-950 px-3 py-2 text-sm font-black uppercase tracking-[0.12em] text-white transition-colors duration-200 hover:bg-white hover:text-gray-950 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-gray-950 disabled:hover:text-white"
            aria-label="Send message"
          >
            <Send className="h-4 w-4" aria-hidden="true" />
            <span>Send</span>
          </button>
        </form>
      )}
    </div>
  )
}

export default function ChatWidget({floating, opaque, ...props}: ChatWidgetProps) {
  const [open, setOpen] = useState(false)

  if (!floating) {
    return (
      <div className="max-w-lg">
        <ChatPanel {...props} />
      </div>
    )
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3 sm:bottom-6 sm:right-6">
      {open && (
        <div className={`${opaque ? "bg-white" : "glass-strong"} w-[min(360px,calc(100vw-2rem))] animate-in fade-in slide-in-from-bottom-4 duration-200`}>
          <div className="flex items-center justify-between border-2 border-b-0 border-gray-950 bg-white px-4 py-3">
            <span className="text-sm font-black uppercase tracking-[0.16em] text-gray-950">Chat with my resume</span>
            <button
              onClick={() => setOpen(false)}
              className="cursor-pointer text-gray-500 transition-colors hover:text-gray-950"
              aria-label="Close chat"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          <ChatPanel {...props} />
        </div>
      )}

        <button
          onClick={() => setOpen(prev => !prev)}
          className="flex h-14 w-14 cursor-pointer items-center justify-center rounded-sm border-2 border-gray-950 bg-gray-950 text-white shadow-[5px_5px_0_rgb(17_24_39_/_0.2)] transition-colors duration-200 hover:bg-white hover:text-gray-950"
          aria-label={open ? "Close chat" : "Open chat"}
        >
        {open ? (
          <X className="h-6 w-6" aria-hidden="true" />
        ) : (
          <MessageCircle className="h-6 w-6" aria-hidden="true" />
        )}
      </button>
    </div>
  )
}
